// skipcq: JS-W1028
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Outlet, useParams } from 'react-router-dom';

import { BrewhouseBeerCatalogProvider, BrewhouseBeerCatalogContext } from '../../context/BrewhouseBeerCatalogContext';
import { BrewerySessionContext } from '../../context/BrewerySessionContext';
import { useBrewhousePageHeaderRegistration } from '../brewhouse/BrewhousePageHeaderContext';
import { BREWHOUSE_PAGE_SHELL_SX } from '../brewhouse/BrewhousePageShell';
import { useBrewhousePortfolioSearch } from '../../hooks/useBrewhousePortfolioSearch';
import { usePintPortfolioCatalogFocusRestore } from '../../hooks/usePintPortfolioCatalogFocusRestore';
import {
  trackPintPortfolioClickAddBeer,
  trackPintPortfolioFilterStyleCategory,
  trackPintPortfolioFilterStyleName,
  trackPintPortfolioSaveBeerAdd,
  trackPintPortfolioSearch,
  trackPintPortfolioSort,
} from '../../utils/brewhousePortalAnalytics';
import {
  DEFAULT_PORTFOLIO_SORT,
  sortPortfolioBeers,
} from '../../utils/brewhousePortfolio/portfolioBeerSort';
import {
  applyStyleFilters,
  getDistinctStyleCategories,
  getDistinctStyleNames,
  PORTFOLIO_STYLE_FILTER_ALL,
} from '../../utils/brewhousePortfolio/portfolioStyleFilters';
import {
  intersectBeersWithSearchIds,
  isPortfolioSearchActive,
} from '../../utils/brewhousePortfolio/portfolioTypesenseSearch';
import PintPortfolioRibbon from './PintPortfolioRibbon';
import PintPortfolioStatusTracker from './PintPortfolioStatusTracker';
import AddBeerDialog from '../beer/AddBeerDialog';

function PintPortfolioLayoutContent() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { beerId: profileBeerId = '' } = useParams();
  const isProfileRoute = profileBeerId.length > 0;
  const { breweryId, activeBreweryName, breweryGroupId } = useContext(BrewerySessionContext);
  const {
    displayBeers,
    locationFilteredBeers,
    loading: catalogLoading,
    error: catalogError,
    portfolioBreweryIds,
    breweryNameById,
    styleMetaMap,
  } = useContext(BrewhouseBeerCatalogContext);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState(DEFAULT_PORTFOLIO_SORT);
  const [styleCategory, setStyleCategory] = useState(PORTFOLIO_STYLE_FILTER_ALL);
  const [styleName, setStyleName] = useState(PORTFOLIO_STYLE_FILTER_ALL);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [addBeerDialogOpen, setAddBeerDialogOpen] = useState(false);
  const lastTrackedSearchRef = useRef('');

  const { beerIds: searchBeerIds, loading: searchLoading, error: searchError } =
    useBrewhousePortfolioSearch(searchQuery, portfolioBreweryIds);

  const styleCategories = useMemo(
    () => getDistinctStyleCategories(locationFilteredBeers, styleMetaMap),
    [locationFilteredBeers, styleMetaMap],
  );

  const styleNames = useMemo(
    () => getDistinctStyleNames(locationFilteredBeers, styleMetaMap),
    [locationFilteredBeers, styleMetaMap],
  );

  const styleFilteredBeers = useMemo(
    () =>
      applyStyleFilters(
        locationFilteredBeers,
        styleMetaMap,
        styleCategory,
        styleName,
      ),
    [locationFilteredBeers, styleMetaMap, styleCategory, styleName],
  );

  const searchFilteredBeers = useMemo(() => {
    if (!isPortfolioSearchActive(searchQuery)) {
      return styleFilteredBeers;
    }
    return intersectBeersWithSearchIds(styleFilteredBeers, searchBeerIds);
  }, [styleFilteredBeers, searchQuery, searchBeerIds]);

  const gridBeers = useMemo(
    () => sortPortfolioBeers(searchFilteredBeers, sortMode),
    [searchFilteredBeers, sortMode],
  );

  const { focusBeerId, clearFocusBeerId } = usePintPortfolioCatalogFocusRestore({
    isProfileRoute,
    profileBeerId,
    breweryId,
  });

  const profileBeerName = useMemo(() => {
    if (!isProfileRoute) {
      return '';
    }
    const candidates = [
      gridBeers.find((beer) => beer.id === profileBeerId),
      locationFilteredBeers.find((beer) => beer.id === profileBeerId),
      displayBeers.find((beer) => beer.id === profileBeerId),
    ];
    const match = candidates.find((beer) => beer && typeof beer.name === 'string');
    return match?.name || '';
  }, [isProfileRoute, profileBeerId, gridBeers, locationFilteredBeers, displayBeers]);

  useEffect(() => {
    if (!isPortfolioSearchActive(searchQuery)) {
      lastTrackedSearchRef.current = '';
      return;
    }
    if (!breweryId) {
      return;
    }
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2 || trimmed === lastTrackedSearchRef.current) {
      return;
    }
    lastTrackedSearchRef.current = trimmed;
    trackPintPortfolioSearch({ breweryId, query: trimmed });
  }, [searchQuery, breweryId]);

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleSortChange = useCallback(
    (event) => {
      const nextSort = event.target.value;
      setSortMode(nextSort);
      if (breweryId) {
        trackPintPortfolioSort({ breweryId, sortMode: nextSort });
      }
    },
    [breweryId],
  );

  const handleStyleCategoryChange = useCallback(
    (event) => {
      const nextCategory = event.target.value;
      setStyleCategory(nextCategory);
      if (breweryId) {
        trackPintPortfolioFilterStyleCategory({ breweryId, category: nextCategory });
      }
    },
    [breweryId],
  );

  const handleStyleNameChange = useCallback(
    (event) => {
      const nextName = event.target.value;
      setStyleName(nextName);
      if (breweryId) {
        trackPintPortfolioFilterStyleName({ breweryId, styleName: nextName });
      }
    },
    [breweryId],
  );

  const handleFilterSheetOpen = useCallback(() => {
    setFilterSheetOpen(true);
  }, []);

  const handleFilterSheetClose = useCallback(() => {
    setFilterSheetOpen(false);
  }, []);

  const handleAddBeerOpen = useCallback(() => {
    if (breweryId) {
      try {
        trackPintPortfolioClickAddBeer({ breweryId });
      } catch {
        // Opening the dialog must succeed even when analytics capture fails.
      }
    }
    setAddBeerDialogOpen(true);
  }, [breweryId]);

  const handleAddBeerClose = useCallback(() => {
    setAddBeerDialogOpen(false);
  }, []);

  const handleAddBeerSuccess = useCallback(
    (result) => {
      if (breweryId) {
        try {
          trackPintPortfolioSaveBeerAdd({
            breweryId,
            beerId: result?.beerId || '',
          });
        } catch {
          // Save success UI must not fail on analytics errors.
        }
      }
      setAddBeerDialogOpen(false);
    },
    [breweryId],
  );

  const breadcrumbSegments = useMemo(() => {
    const segments = [
      { label: 'Brewhouse', to: '/brewery-portal/dashboard' },
      { label: 'Pint Portfolio', to: isProfileRoute ? '/brewery-portal/pint-portfolio' : undefined },
    ];
    if (isProfileRoute && profileBeerName.length > 0) {
      segments.push({ label: profileBeerName });
    }
    return segments;
  }, [isProfileRoute, profileBeerName]);

  const outletContext = useMemo(
    () => ({
      breweryId,
      gridBeers,
      catalogLoading,
      catalogError: catalogError || searchError,
      searchLoading,
      styleMetaMap,
      breweryNameById,
      focusBeerId,
      clearFocusBeerId,
    }),
    [
      breweryId,
      gridBeers,
      catalogLoading,
      catalogError,
      searchError,
      searchLoading,
      styleMetaMap,
      breweryNameById,
      focusBeerId,
      clearFocusBeerId,
    ],
  );

  useBrewhousePageHeaderRegistration({
    breadcrumbs: breadcrumbSegments,
    backLink: {
      to: isProfileRoute ? '/brewery-portal/pint-portfolio' : '/brewery-portal/dashboard',
      label: isProfileRoute ? 'Back to Pint Portfolio' : 'Back to Brewhouse',
    },
    title: isProfileRoute
      ? null
      : `Pint Portfolio (${gridBeers.length} ${gridBeers.length === 1 ? 'Beer' : 'Beers'})`,
  });

  return (
    <Box sx={BREWHOUSE_PAGE_SHELL_SX}>
      {!isProfileRoute && (
        <PintPortfolioRibbon
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          sortMode={sortMode}
          onSortChange={handleSortChange}
          styleCategory={styleCategory}
          onStyleCategoryChange={handleStyleCategoryChange}
          styleCategories={styleCategories}
          styleName={styleName}
          onStyleNameChange={handleStyleNameChange}
          styleNames={styleNames}
          filterSheetOpen={filterSheetOpen}
          onFilterSheetOpen={handleFilterSheetOpen}
          onFilterSheetClose={handleFilterSheetClose}
          onAddBeerClick={handleAddBeerOpen}
        />
      )}

      {!isProfileRoute ? (
        <AddBeerDialog
          open={addBeerDialogOpen}
          onClose={handleAddBeerClose}
          lockedBreweryId={breweryId || ''}
          lockedBreweryName={activeBreweryName || ''}
          breweryGroupId={breweryGroupId || null}
          onSuccess={handleAddBeerSuccess}
          sourceType="brewhouse_user"
        />
      ) : null}

      {!isProfileRoute && !isMdUp && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <PintPortfolioStatusTracker
            beers={locationFilteredBeers}
            loading={catalogLoading}
            layout="band"
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { md: 'flex-start' },
          mt: isProfileRoute ? 0 : 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Outlet context={outletContext} />
        </Box>

        {!isProfileRoute && isMdUp && (
          <Box sx={{ flexShrink: 0 }}>
            <PintPortfolioStatusTracker
              beers={locationFilteredBeers}
              loading={catalogLoading}
              layout="rail"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PintPortfolioLayout() {
  return (
    <BrewhouseBeerCatalogProvider>
      <PintPortfolioLayoutContent />
    </BrewhouseBeerCatalogProvider>
  );
}

export default PintPortfolioLayout;
